<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";

// Store
import { useGroupStore } from "@/stores/useGroupStore";
import { useUserStore } from "@/stores/useUserStore";

// Componentes modais
import AddGroupForm from "./AddGroupForm.vue";
import EditGroupForm from "./EditGroupForm.vue";
import DeleteGroupForm from "./DeleteGroupForm.vue";
import GroupCardModal from "./GroupCardModal.vue";
import BatchDeleteGroupForm from "./BatchDeleteGroupForm.vue";
import SearchGroupInput from "./SearchGroupInput.vue";
import GroupFormHelpModal from "./GroupFormHelpModal.vue";

// Paginação
import PaginationButtons from "../Generics/PaginationButtons.vue";
import PaginationInfo from "../Generics/PaginationInfo.vue";

// Store
const groupStore = useGroupStore();
const userStore = useUserStore();

// Estados de exibição dos modais
const showAddModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showBatchDeleteModal = ref(false);

const showCardModal = ref(false);
const selectedGroupId = ref(null);

// Modal de ajuda
const showHelpModal = ref(false);

// Paginação
const currentPage = ref(1);
const pageSize = ref(10);

// ✅ EVITA "FLASH" DE LISTA COMPLETA
const groupsToRender = ref([]);
const isTableReady = ref(false);
const isLoadingTable = ref(false);

// Lógica de Dropdowns e Seleção
const openDropdownId = ref(null);
const isBatchDropdownOpen = ref(false);
const batchDropdownRef = ref(null);

// ====================================================================
// ✅ TOAST PADRÃO
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 3000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function handleChildNotify(payload) {
  if (!payload) return;

  if (payload.type === "success") {
    showSuccessToast(payload.message || "Operação realizada com sucesso.");
    return;
  }

  showErrorToast(payload.message || "Ocorreu um erro.");
}

async function handleGroupChanged() {
  await handleGroupsChanged({
    goToFirstPage: false,
    closeAdd: false,
    closeEdit: false,
    closeDelete: false,
    closeBatchDelete: false,
    clearSelection: false,
  });
}

async function handleGroupDeleted() {
  await handleGroupsChanged({
    goToFirstPage: true,
    closeAdd: false,
    closeEdit: false,
    closeDelete: true,
    closeBatchDelete: true,
    clearSelection: true,
  });
}
// ====================================================================

function normalizeUserName(name) {
  return (name ?? "").toString().trim().toLowerCase();
}

function isLoggedSys() {
  return normalizeUserName(userStore.currentUser?.userName) === "sys";
}

function isCreatedBySys(group) {
  return normalizeUserName(group?.createdByUserName) === "sys";
}

// Grupos mantidos pelo sistema ("Todos", "Alunos", "Professores" do círculo):
// nome, pai e composição são dele, então nem Sys edita ou apaga por aqui.
function isSystemGroup(group) {
  return !!(group?.systemRole ?? group?.SystemRole);
}

function canManage(group) {
  if (isSystemGroup(group)) return false;

  const logged = (userStore.currentUser?.userName ?? "").trim().toLowerCase();
  if (!logged) return false;

  if (logged === "sys") return true;

  const creator = (group?.createdByUserName ?? "").trim().toLowerCase();
  if (!creator) return true;

  return creator !== "sys";
}

function sysProtectedTooltip(group) {
  if (!group) return "";
  if (isSystemGroup(group)) return "Mantido pelo sistema: a composição vem dos vínculos";
  if (isLoggedSys()) return "";
  if (!isCreatedBySys(group)) return "";
  return "Protegido (criado pelo Sys)";
}

const isAllSelectedOnPage = computed({
  get: () => {
    const manageableIdsOnPage = groupsToRender.value
      .filter((g) => canManage(g))
      .map((g) => g.groupId);

    if (manageableIdsOnPage.length === 0) return false;

    return manageableIdsOnPage.every((id) =>
      groupStore.selectedGroupIds.includes(id)
    );
  },
  set: (value) => {
    const manageableIdsOnPage = groupsToRender.value
      .filter((g) => canManage(g))
      .map((g) => g.groupId);

    const allIdsOnPage = groupsToRender.value.map((g) => g.groupId);

    if (value) {
      manageableIdsOnPage.forEach((id) => {
        if (!groupStore.selectedGroupIds.includes(id)) {
          groupStore.selectedGroupIds.push(id);
        }
      });
    } else {
      groupStore.selectedGroupIds = groupStore.selectedGroupIds.filter(
        (id) => !allIdsOnPage.includes(id)
      );
    }
  },
});

const anyEditableOnPage = computed(() =>
  groupsToRender.value.some((g) => canManage(g))
);

const canBatchActions = computed(() => {
  if (!Array.isArray(groupStore.selectedGroupIds)) return false;
  return groupStore.selectedGroupIds.length > 0;
});

function toggleRowDropdown(groupId) {
  openDropdownId.value = openDropdownId.value === groupId ? null : groupId;
}

function handleEdit(index) {
  indexSelector(index);
  showEditModal.value = true;
  openDropdownId.value = null;
}

function handleView(index) {
  handleOpenCard(index);
  openDropdownId.value = null;
}

function handleOpenCard(index) {
  const group = groupsToRender.value[index];
  if (!group) return;

  selectedGroupId.value = group.groupId;
  groupStore.setSelectedGroup(group);
  showCardModal.value = true;
}

function handleDelete(index) {
  indexSelector(index);
  showDeleteModal.value = true;
  openDropdownId.value = null;
}

function toggleSelection(id) {
  groupStore.toggleGroupSelection(id);
}

// Fetch de dados
async function fetchGroups() {
  try {
    isLoadingTable.value = true;

    if (!isTableReady.value) groupsToRender.value = [];

    await groupStore.fetchGroupsPaged(
      currentPage.value,
      pageSize.value,
      groupStore.groupSearchTerm
    );

    groupsToRender.value = Array.isArray(groupStore.groups)
      ? [...groupStore.groups]
      : [];

    isTableReady.value = true;

    if (!isLoggedSys() && groupsToRender.value.length) {
      const sysIdsOnPage = groupsToRender.value
        .filter((g) => isCreatedBySys(g))
        .map((g) => g.groupId);

      if (sysIdsOnPage.length) {
        groupStore.selectedGroupIds = groupStore.selectedGroupIds.filter(
          (id) => !sysIdsOnPage.includes(id)
        );
      }
    }

    if (groupStore.shouldForcePageOne) {
      groupStore.shouldForcePageOne = false;

      if (currentPage.value !== 1) {
        currentPage.value = 1;
      } else {
        await groupStore.fetchGroupsPaged(
          1,
          pageSize.value,
          groupStore.groupSearchTerm
        );

        groupsToRender.value = Array.isArray(groupStore.groups)
          ? [...groupStore.groups]
          : [];

        if (!isLoggedSys() && groupsToRender.value.length) {
          const sysIdsOnPage = groupsToRender.value
            .filter((g) => isCreatedBySys(g))
            .map((g) => g.groupId);

          if (sysIdsOnPage.length) {
            groupStore.selectedGroupIds = groupStore.selectedGroupIds.filter(
              (id) => !sysIdsOnPage.includes(id)
            );
          }
        }
      }
    }
  } catch (error) {
    console.error("❌ Erro ao buscar grupos paginados:", error?.message);
    showErrorToast("Erro ao carregar grupos.");
  } finally {
    isLoadingTable.value = false;
  }
}

watch(currentPage, fetchGroups);

watch(
  () => groupStore.groupSearchTerm,
  () => {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
    } else {
      fetchGroups();
    }
  }
);

watch(
  () => groupStore.groups,
  (val) => {
    if (!isTableReady.value) return;
    groupsToRender.value = Array.isArray(val) ? [...val] : [];

    if (!isLoggedSys() && groupsToRender.value.length) {
      const sysIdsOnPage = groupsToRender.value
        .filter((g) => isCreatedBySys(g))
        .map((g) => g.groupId);

      if (sysIdsOnPage.length) {
        groupStore.selectedGroupIds = groupStore.selectedGroupIds.filter(
          (id) => !sysIdsOnPage.includes(id)
        );
      }
    }
  }
);

function tryOpenBatchDeleteModal() {
  const selectedIds = groupStore.selectedGroupIds;

  if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
    showErrorToast("Nenhum grupo selecionado.");
    isBatchDropdownOpen.value = false;
    return;
  }

  showBatchDeleteModal.value = true;
  isBatchDropdownOpen.value = false;
}

// ====================================================================
// ✅ Handler único pós-mudança
// ====================================================================
async function handleGroupsChanged(options = {}) {
  const {
    goToFirstPage = true,
    closeAdd = true,
    closeEdit = true,
    closeDelete = true,
    closeBatchDelete = true,
    clearSelection = false,
    successMessage = null,
  } = options;

  if (closeAdd) showAddModal.value = false;
  if (closeEdit) showEditModal.value = false;
  if (closeDelete) showDeleteModal.value = false;
  if (closeBatchDelete) showBatchDeleteModal.value = false;

  if (clearSelection) {
    groupStore.selectedGroupIds = [];
  }

  if (successMessage) {
    showSuccessToast(successMessage);
  }

  if (goToFirstPage) {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
      return;
    }
    await fetchGroups();
    return;
  }

  await fetchGroups();
}
// ====================================================================

function handleClickOutside(event) {
  if (
    batchDropdownRef.value &&
    !batchDropdownRef.value.contains(event.target)
  ) {
    isBatchDropdownOpen.value = false;
  }
  if (openDropdownId.value && !event.target.closest(".action-cell-container")) {
    openDropdownId.value = null;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  fetchGroups();
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);

  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);

  groupStore.groupSearchTerm = "";
  groupStore.currentPage = 1;
  groupStore.shouldForcePageOne = true;

  groupStore.selectedGroupIds = [];
  groupStore.selectedGroup = null;
  openDropdownId.value = null;
  isBatchDropdownOpen.value = false;
  selectedGroupId.value = null;

  showAddModal.value = false;
  showEditModal.value = false;
  showDeleteModal.value = false;
  showBatchDeleteModal.value = false;
  showCardModal.value = false;
  showHelpModal.value = false;

  groupsToRender.value = [];
  isTableReady.value = false;
  isLoadingTable.value = false;
});

function indexSelector(index) {
  const group = groupsToRender.value[index];
  if (!group) return;
  groupStore.setSelectedGroup(group);
}
</script>

<template>
  <div class="w-full h-full">
    <div class="main-container">
      <div class="main-header">
        <div class="flex flex-col gap-0">
          <div class="text-xl font-bold">Grupos</div>
          <div class="text-sm mt-1 text-gray-700 ">
            Crie grupos, adicione pessoas a eles e aplique as permissões de
            acesso a textos, atividades e notas para todos do grupo.
            <button
              class="underline text-blue-600 hover:text-blue-800 ml-1"
              @click="showHelpModal = true"
            >
              Saiba mais
            </button>
          </div>
        </div>
      </div>

      <div class="action-bar">
        <div class="w-full md:w-1/2">
          <SearchGroupInput />
        </div>
        <div
          class="w-full md:w-auto flex flex-col md:flex-row space-y-2 md:space-y-0 items-stretch md:items-center justify-end md:space-x-3 flex-shrink-0"
        >
          <button @click="showAddModal = true" class="btn btn-primary">
            <svg class="h-4 w-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
              <path
                clip-rule="evenodd"
                fill-rule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              />
            </svg>
            Adicionar Grupo
          </button>

          <div ref="batchDropdownRef" class="relative">
            <button
              @click="isBatchDropdownOpen = !isBatchDropdownOpen"
              class="btn btn-batch w-full"
              :disabled="!canBatchActions"
            >
              Ações em lote
              <svg
                class="-mr-1 ml-1.5 w-5 h-5 batch-chevron"
                :class="{ open: isBatchDropdownOpen }"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  clip-rule="evenodd"
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                />
              </svg>
            </button>

            <div v-if="isBatchDropdownOpen" class="dropdown-menu">
              <div class="py-1">
                <a
                  href="#"
                  role="button"
                  @click.prevent="tryOpenBatchDeleteModal"
                  class="dropdown-item danger-item"
                >
                  <i class="fas fa-trash-alt"></i>
                  <span>Apagar selecionados</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="table-wrapper">
        <table class="main-table">
          <thead>
            <tr>
              <th style="width: 4%">
                <input
                  id="checkbox-all"
                  type="checkbox"
                  v-model="isAllSelectedOnPage"
                  class="checkbox-style"
                  :disabled="!anyEditableOnPage"
                  :class="{ 'disabled-item': !anyEditableOnPage }"
                />
              </th>
              <th style="width: 60px">Ações</th>
              <th style="width: 5%">Id</th>
              <th style="width: 22%">Nome</th>
              <th style="width: 23%">Descrição</th>
              <th style="width: 10%">Criador</th>
              <th style="width: 10%">Atualizador</th>
              <th style="width: 13%">Criação</th>
              <th style="width: 13%">Atualização</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(group, index) in !isLoadingTable ? groupsToRender : []"
              :key="group.groupId"
              @click="handleOpenCard(index)"
              class="clickable-row"
            >
              <td
                @click.stop
                :class="{ 'checkbox-cell-disabled': !canManage(group) }"
              >
                <input
                  type="checkbox"
                  :value="group.groupId"
                  :checked="groupStore.selectedGroupIds?.includes(group.groupId)"
                  @change="toggleSelection(group.groupId)"
                  class="checkbox-style"
                  :class="{ 'checkbox-disabled': !canManage(group) }"
                  :disabled="!canManage(group)"
                  :title="sysProtectedTooltip(group)"
                />
              </td>

              <td @click.stop class="action-cell-container">
                <button
                  @click="toggleRowDropdown(group.groupId)"
                  class="btn-icon btn-soft-brand"
                  title="Menu de Ações"
                >
                  <i class="fas fa-ellipsis-h fa-lg"></i>
                </button>

                <div
                  v-if="openDropdownId === group.groupId"
                  class="row-dropdown-menu"
                >
                  <ul>
                    <li
                      :class="{ 'disabled-item': !canManage(group) }"
                      @click="canManage(group) && handleEdit(index)"
                    >
                      <i class="fas fa-pencil-alt"></i><span>Editar</span>
                    </li>

                    <li @click="handleView(index)">
                      <i class="fas fa-eye"></i><span>Visualizar</span>
                    </li>

                    <li
                      class="danger-item"
                      :class="{ 'disabled-item': !canManage(group) }"
                      @click="canManage(group) && handleDelete(index)"
                    >
                      <i class="fas fa-trash-alt"></i><span>Apagar</span>
                    </li>
                  </ul>
                </div>
              </td>

              <td>
                <span :title="group.groupId">{{ group.groupId }}</span>
              </td>
              <td class="cell-text-center">
                <span :title="group.groupName">{{ group.groupName }}</span>
              </td>
              <td class="cell-text-center">
                <span :title="group.description">{{ group.description }}</span>
              </td>
              <td class="cell-text-center">
                <span :title="group.createdByUserName">
                  {{ group.createdByUserName }}
                </span>
              </td>
              <td class="cell-text-center">
                <span :title="group.updatedByUserName">
                  {{ group.updatedByUserName }}
                </span>
              </td>
              <td>
                <span :title="formatDateTimeForDisplay(group.createdAt)">
                  {{ formatDateTimeForDisplay(group.createdAt) }}
                </span>
              </td>
              <td>
                <span :title="formatDateTimeForDisplay(group.updatedAt)">
                  {{ formatDateTimeForDisplay(group.updatedAt) }}
                </span>
              </td>
            </tr>

            <tr v-if="isLoadingTable" class="empty-row-state">
              <td colspan="9" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <svg class="empty-spinner" viewBox="0 0 24 24" fill="none">
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <p>Carregando grupos...</p>
                </div>
              </td>
            </tr>

            <tr
              v-else-if="groupsToRender.length === 0"
              class="empty-row-state"
            >
              <td colspan="9" class="empty-row empty-row-state">
                <div class="empty-state-content">
                  <img src="/img/lupa.webp" alt="" width="38" height="40" class="empty-icon-img" />
                  <p>Nenhum grupo encontrado.</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="!isLoadingTable && (groupStore.totalGroupsCount ?? 0) > 0"
          class="group-pagination-row"
        >
          <div class="group-pagination-left">
            <span class="group-pagination-label">Mostrando</span>
            <PaginationInfo
              :current-page="currentPage"
              :page-size="pageSize"
              :total-count="groupStore.totalGroupsCount ?? 0"
            />
            <span>grupos</span>
          </div>

          <PaginationButtons
            :current-page="currentPage"
            :page-size="pageSize"
            :total-count="groupStore.totalGroupsCount ?? 0"
            :total-pages="groupStore.totalGroupsPages"
            @change-page="(page) => (currentPage = page)"
          />
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <AddGroupForm
      v-if="showAddModal"
      @close="showAddModal = false"
      @refresh="handleGroupsChanged({ goToFirstPage: true, closeAdd: true })"
    />

    <EditGroupForm
      v-if="showEditModal"
      :groupId="groupStore.selectedGroup?.groupId"
      @close="showEditModal = false"
      @refresh="handleGroupsChanged({ goToFirstPage: false, closeEdit: true })"
    />

    <GroupCardModal
      v-if="showCardModal"
      :show="showCardModal"
      :group-id="selectedGroupId"
      @close="showCardModal = false"
      @group-updated="handleGroupChanged"
      @notify="handleChildNotify"
    />

    <DeleteGroupForm
      v-if="showDeleteModal"
      :group="groupStore.selectedGroup"
      @close="showDeleteModal = false"
      @refresh="handleGroupDeleted"
      @notify="handleChildNotify"
    />

    <BatchDeleteGroupForm
      v-if="showBatchDeleteModal"
      @close="showBatchDeleteModal = false"
      @refresh="handleGroupDeleted"
      @notify="handleChildNotify"
    />

    <GroupFormHelpModal
      v-if="showHelpModal"
      :show="showHelpModal"
      @close="showHelpModal = false"
    />
  </Teleport>

  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            &times;
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
@media (min-width: 768px) {
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
}

.main-table .cell-text-center {
  text-align: center;
}

.checkbox-cell-disabled * {
  cursor: not-allowed !important;
}

.group-pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem 1rem;
  font-size: 0.82rem;
}
.group-pagination-left {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.group-pagination-label {
  padding-top: 2px;
}

.empty-state-content {
  width: 100%;
  margin: 0 auto;
}

</style>